/* quotestate — what the DOCUMENT is doing, and separately what the MONEY is
   doing. They were one field, and it produced two lies at once:

     · addQuote() seeded `sends: [{ at: createdAt }]` and `lastSentAt` the
       moment a quote was created, so every estimate was born "sent" — and the
       list added its own synthetic fallback on top, so even a quote with no
       sends array showed one. A quick-add draft nobody had sent entered the
       follow-up chase as though the customer had gone quiet on it.
     · a single "paid" status covered a paid deposit and a settled invoice
       alike, so a job with a balance outstanding read Paid.

   Document state answers "where is this with the customer". Payment state
   answers "how much of it is money". A quote can be Accepted and Deposit paid,
   or Sent and Deposit due, and neither implies the other. */

export type DocState = "draft" | "sent" | "viewed" | "accepted" | "declined" | "expired";
export type PayState = "none" | "deposit-due" | "deposit-paid" | "balance-due" | "paid";

export const DOC_LABEL: Record<DocState, string> = {
  draft: "Draft", sent: "Sent", viewed: "Viewed",
  accepted: "Accepted", declined: "Declined", expired: "Expired",
};
export const PAY_LABEL: Record<PayState, string> = {
  none: "", "deposit-due": "Deposit due", "deposit-paid": "Deposit paid",
  "balance-due": "Balance due", paid: "Paid in full",
};

/** sends actually logged. Never invented from createdAt. */
export function sendsOf(q: any): any[] {
  return Array.isArray(q && q.sends) ? q.sends : [];
}
export function wasSent(q: any) {
  return sendsOf(q).length > 0 || !!(q && q.lastSentAt);
}
/** when it last went out, or null when it never did — not createdAt */
export function lastSentOf(q: any): number | null {
  const s = sendsOf(q);
  const n = Number(q && q.lastSentAt) || (s.length ? Number(s[s.length - 1].at) : 0);
  return n > 0 ? n : null;
}

export function docState(q: any): DocState {
  if (!q) return "draft";
  const st = String(q.status || "");
  if (st === "lost") return "declined";
  if (!wasSent(q)) return "draft";                   // never sent: not in play yet
  if (st === "signed" || st === "paid" || q.signedAt) return "accepted";
  const exp = Number(q.expiresAt) || 0;
  if (exp > 0 && exp < Date.now()) return "expired";
  // q.validUntil (lib/quotedetails.ts) is a DATE: good through that day,
  // expired from the next — read on this device's calendar (it's the
  // contractor's own screen; the server judges signing on the business's zone)
  if (typeof q.validUntil === "string" && /^\d{4}-\d{2}-\d{2}$/.test(q.validUntil)) {
    const d = new Date();
    const today = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
    if (q.validUntil < today) return "expired";
  }
  if (st === "viewed" || q.viewedAt) return "viewed";
  return "sent";
}

export function paidOf(q: any) {
  const manual = (Array.isArray(q && q.payments) ? q.payments : []).reduce((s: number, p: any) => s + (Number(p.amount) || 0), 0);
  return Math.round(((Number(q && q.depositPaid) || 0) + manual) * 100) / 100;
}

/* The money, with the deposit and the balance kept apart. `depositTarget` is
   what the quote asked for up front (settings' deposit % when the quote carries
   no explicit figure); 0 means no deposit was asked for. */
export function payState(q: any, settings?: any): { state: PayState; paid: number; total: number; balance: number; label: string } {
  const total = Math.round((Number(q && q.total) || 0) * 100) / 100;
  const paid = paidOf(q);
  const balance = Math.round(Math.max(0, total - paid) * 100) / 100;
  const pct = Number((settings && settings.depositPct) != null ? settings.depositPct : 0) || 0;
  const target = Number(q && q.depositTarget) || (pct > 0 ? Math.round(total * (pct > 1 ? pct / 100 : pct) * 100) / 100 : 0);

  let state: PayState;
  if (total <= 0) state = "none";
  else if (balance <= 0.005 && paid > 0) state = "paid";
  else if (paid <= 0) state = target > 0 && docState(q) === "accepted" ? "deposit-due" : "none";
  else if (target > 0 && paid + 0.005 >= target) state = "balance-due";
  else state = "deposit-paid";

  return { state, paid, total, balance, label: PAY_LABEL[state] };
}

/* Is this quote in the follow-up chase? A draft is not: nobody has gone quiet
   on something they were never sent. */
export function inFollowUp(q: any) {
  const d = docState(q);
  return d === "sent" || d === "viewed";
}
